import { ArrowRight, MapPin, Phone, Timer } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { PublicOffer } from "./publicOffer.types";
import { PUBLIC_OFFER_CTA_BUTTON_CLASS } from "./publicOffer.utils";
import { OfferCardMedia, OfferTimeLeftLabel } from "./PublicOfferMedia";

type PublicOfferDetailModalProps = {
  offer: PublicOffer | null;
  onOpenChange: (open: boolean) => void;
};

export function PublicOfferDetailModal({
  offer,
  onOpenChange,
}: PublicOfferDetailModalProps) {
  return (
    <Dialog open={offer !== null} onOpenChange={onOpenChange}>
      {offer ? (
        <DialogContent
          className={cn(
            "max-h-[min(90vh,760px)] gap-0 overflow-y-auto bg-white p-0 sm:max-w-3xl rounded-none sm:rounded-none",
            "border border-slate-200/80 shadow-2xl shadow-slate-900/10",
          )}
        >
          <div className="group overflow-hidden rounded-none bg-white">
            <OfferCardMedia
              offer={offer}
              imageUrl={offer.detailImageUrl || offer.cardImageUrl}
              hoverZoom={false}
              imageFit="cover"
              variant="card"
            />
          </div>

          <div className="space-y-4 bg-white p-6 pb-4">
            {offer.cardImageUrl ? (
              <div className="flex justify-center">
                <div className="border border-slate-200 bg-white px-5 py-2.5 shadow-sm">
                  <img
                    src={offer.cardImageUrl}
                    alt={offer.merchantName || "Merchant"}
                    className="h-7 max-w-[160px] object-contain"
                  />
                </div>
              </div>
            ) : null}

            <DialogHeader className="gap-2 text-left">
              {offer.categoryName ? (
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
                  {offer.categoryName.toUpperCase()}
                </p>
              ) : null}
              <DialogTitle className="text-xl font-bold leading-snug text-slate-900">
                {offer.title}
              </DialogTitle>
              {offer.merchantName ? (
                <p className="text-sm font-normal text-slate-400">
                  By {offer.merchantName}
                </p>
              ) : null}
            </DialogHeader>

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Offer description
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                {offer.description}
              </p>
            </div>

            <Accordion type="multiple" className="w-full bg-white">
              <AccordionItem
                value="validity"
                className="border-0 border-t border-b border-slate-200"
              >
                <AccordionTrigger
                  icon="plus"
                  className="py-5 text-base font-medium text-slate-800 hover:no-underline hover:text-slate-900"
                >
                  Validity
                </AccordionTrigger>
                <AccordionContent className="pb-5">
                  <div
                    className="flex gap-3 text-slate-800"
                    role="status"
                    aria-live="polite"
                  >
                    <Timer
                      className="mt-0.5 h-5 w-5 shrink-0 text-violet-600"
                      aria-hidden
                    />
                    <div>
                      <p className="mt-0">
                        <OfferTimeLeftLabel
                          expiresAtMillis={offer.expiresAtMillis}
                          variant="card"
                        />
                      </p>
                      <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                        <span>{offer.startDate}</span>
                        <span className="text-slate-300" aria-hidden>
                          ·
                        </span>
                        <span>{offer.expiry}</span>
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {offer.addressDetails && offer.addressDetails.length > 0 ? (
                <AccordionItem
                  value="locations"
                  className="border-0 border-b border-slate-200"
                >
                  <AccordionTrigger
                    icon="plus"
                    className="py-5 text-base font-medium text-slate-800 hover:no-underline hover:text-slate-900"
                  >
                    Locations
                    <span className="ml-2 font-normal text-slate-400">
                      ({offer.addressDetails.length})
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <ul className="space-y-4">
                      {offer.addressDetails.map((addr, idx) => (
                        <li
                          key={addr.id ?? idx}
                          className="space-y-1.5 border-b border-slate-100 pb-4 text-sm text-slate-700 last:mb-0 last:border-0 last:pb-0"
                        >
                          {addr.location ? (
                            <p className="flex items-start gap-2 font-medium text-slate-800">
                              <MapPin
                                className="mt-0.5 h-4 w-4 shrink-0 text-violet-600"
                                aria-hidden
                              />
                              {addr.location}
                            </p>
                          ) : null}
                          {addr.address ? (
                            <p className="pl-6 text-slate-700">
                              {addr.address}
                            </p>
                          ) : null}
                          {(addr.city || addr.country) && (
                            <p className="pl-6 text-slate-600">
                              {[addr.city, addr.country]
                                .filter(Boolean)
                                .join(", ")}
                            </p>
                          )}
                          {addr.phoneNumber ? (
                            <p className="flex items-center gap-2 pl-6 text-slate-600">
                              <Phone
                                className="h-3.5 w-3.5 shrink-0"
                                aria-hidden
                              />
                              <span>
                                {addr.phoneCountryCode
                                  ? `${addr.phoneCountryCode} `
                                  : ""}
                                {addr.phoneNumber}
                              </span>
                            </p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ) : null}

              {offer.termsLines && offer.termsLines.length > 0 ? (
                <AccordionItem
                  value="terms"
                  className="border-0 border-b border-slate-200"
                >
                  <AccordionTrigger
                    icon="plus"
                    className="py-5 text-base font-medium text-slate-800 hover:no-underline hover:text-slate-900"
                  >
                    Terms & Conditions
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-slate-700">
                      {offer.termsLines.map((line, idx) => (
                        <li key={idx}>{line}</li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ) : null}
            </Accordion>
          </div>

          {offer.offerUrl ? (
            <DialogFooter className="border-t border-slate-200 bg-white p-4 sm:justify-stretch">
              <a
                href={offer.offerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(PUBLIC_OFFER_CTA_BUTTON_CLASS, "!mt-0 w-full")}
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  {offer.cta}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </span>
              </a>
            </DialogFooter>
          ) : null}
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
